import { ElementRef, Directive, ChangeDetectorRef, Input, Renderer2, HostListener, EventEmitter, Injector, Output, OnDestroy } from '@angular/core';
import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay, ConnectedPosition, ScrollStrategy, ScrollStrategyOptions, OverlayRef } from "@angular/cdk/overlay";
import { TOOLTIP_TOKEN, TooltipComponent, TOOLTIP_ID_TOKEN } from './tooltip.component';
import { ScrollDispatcher, CdkScrollable } from '@angular/cdk/scrolling';
let TOOLTIP_ID = 0;

@Directive({
  selector: '[valgTooltip]'
})
export class TooltipDirective implements OnDestroy {
  @Input() valgTooltip = '';
  @Input() positionY: 'below' | 'above' = 'below';
  @Input() positionX: 'start' | 'end' | 'center' = 'end';
  @Input() offsetX: number = 19;
  @Input() offsetY: number = 16;
  @Input() enableTooltipOnClick = true;
  @Output() opened = new EventEmitter<Event>();
  @Output() closed = new EventEmitter<Event>();

  public id = `valg-tooltip-${TOOLTIP_ID++}`;

  overlayRef!: OverlayRef;

  scrollStrategy: ScrollStrategy;

  private belowPosition!: ConnectedPosition;
  private abovePosition!: ConnectedPosition;
  private overlayConnectedPositions!: ConnectedPosition[];

  tooltipPortal!: ComponentPortal<TooltipComponent>;
  public clickedListener!: () => void;

  clicked = false;
  isOpen = false;
  elementIsHovered = false;
  tooltipIsHovered = false;

  constructor(
    private element: ElementRef,
    private overlay: Overlay,
    protected readonly _sso: ScrollStrategyOptions,
    private renderer: Renderer2,
    private sd: ScrollDispatcher,
    private cdr: ChangeDetectorRef
    ) {
      this.scrollStrategy = _sso.reposition();
  }


  setConfiguation(){    
    this.scrollStrategy = this._sso.reposition();
    this.belowPosition = {
      originX: this.positionX,
      originY: 'bottom',
      overlayX: this.positionX,
      overlayY: 'top',
      panelClass: 'valg-tooltip-overlay__below__'+this.positionX,
      offsetY: this.offsetY,
      offsetX: this.offsetX
    } as ConnectedPosition;
  
    this.abovePosition = {
      originX: this.positionX,
      originY: 'top',
      overlayX: this.positionX,
      overlayY: 'bottom',
      panelClass: 'valg-tooltip-overlay__above__'+ this.positionX,
      offsetY: -this.offsetY,
      offsetX: this.offsetX
    } as ConnectedPosition;
  
    this.overlayConnectedPositions = [
      this.belowPosition,
      this.abovePosition
    ];
    
    if(this.positionY === 'above'){
      // @ts-ignore
      this.overlayConnectedPositions = this.overlayConnectedPositions.reverse();
    }
    

    const portalInjector = Injector.create({
      providers: [
        { provide: TOOLTIP_TOKEN, useValue: this.valgTooltip },
        { provide: TOOLTIP_ID_TOKEN, useValue: this.id }],
    });

    this.tooltipPortal = new ComponentPortal(TooltipComponent, null, portalInjector);
  }
  
  open() {
    if(!this.isOpen){
      this.setConfiguation();
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(this.element)
        .withPositions(this.overlayConnectedPositions)
        .withPush(false);
      this.overlayRef = this.overlay.create({
        scrollStrategy: this.scrollStrategy,
        positionStrategy,
        panelClass: "valg-tooltip-overlay"
      });
      
      this.overlayRef.attach(this.tooltipPortal);
      requestAnimationFrame(() => {
        this.overlayRef.updatePosition();
      });
      this.renderer.addClass(this.element.nativeElement, 'tooltip-active');
      this.renderer.setAttribute(this.element.nativeElement, 'aria-describedby', this.id);
      this.isOpen = true;
      this.opened.emit();
      this.cdr.detectChanges();

      
      this.sd.ancestorScrolled(this.element).subscribe(event => {
        let container = (event as CdkScrollable)?.getElementRef().nativeElement;
        if(container?.classList.contains('mat-mdc-dialog-content')){
          let elementTop = this.element.nativeElement.offsetTop;
          let elementScrollPosition = elementTop - container.offsetTop;
          let bottomLine = container.scrollTop + container.offsetHeight;
          let isWithinView = elementScrollPosition >= container.scrollTop && elementScrollPosition <= bottomLine;

          if(!isWithinView){
            this.close();
          }
        }
      });
      
      if(this.overlayRef.overlayElement){
        this.renderer.listen(this.overlayRef.overlayElement, 'mouseenter', () => {
          this.tooltipIsHovered = true;
        });
        this.renderer.listen(this.overlayRef.overlayElement, 'mouseleave', () => {
          this.tooltipIsHovered = false;
          if(!this.clicked){
            this.close();
          }
        });
      }
    }
  }
  
  close(){
    if(this.overlayRef){
      this.overlayRef.dispose();

      if(this.clickedListener){
        this.clickedListener();
      }
      this.isOpen = false;
      this.clicked = false;
      this.elementIsHovered = false;
      this.renderer.removeClass(this.element.nativeElement, 'tooltip-active');
      this.renderer.removeAttribute(this.element.nativeElement, 'aria-describedby');
      this.closed.emit();
    }
  }


  @HostListener('click', ['$event'])
  onClick(event: Event) {
    if(!this.elementIsHovered && this.isOpen){
      this.close();
    }
    else if(this.enableTooltipOnClick){
      event.stopPropagation();

      this.clicked = true;
      this.open();

      this.clickedListener = this.renderer.listen('document', 'click', (evt) => {
        let target = evt.target as HTMLElement;
        if(!target.classList.contains('valg-tooltip')){
          this.close();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.close();
  }

  @HostListener('mouseenter')
  onMouseEnter() {
    if(!this.clicked && !this.isOpen){
      this.elementIsHovered = true;
      this.open(); 
    }
  }

  @HostListener('mouseleave')
  onMouseLeave() {
    if(!this.clicked && this.elementIsHovered){
      setTimeout(() => {
        if(!this.clicked && this.elementIsHovered && !this.tooltipIsHovered){
          this.elementIsHovered = false;
          this.close();
        }
      }, 150);
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.close();
  }
}
